/** @jsx jsx */
import { jsx } from "@theme-ui/core";
import React from "react";
import {
  useDialogState,
  Dialog,
  DialogDisclosure,
  DialogBackdrop,
} from "reakit";
import { motion, useAnimation } from "framer-motion";
import { useResponsiveValue } from "@theme-ui/match-media";

import { ReactComponent as Hamburger } from "../../../images/icons/hamburger.svg";
import { ReactComponent as Close } from "../../../images/icons/close.svg";

import Box from "../Box";
import List from "../List";
import Link from "../Link";
import GridRow from "../GridRow";
import Button from "../Button";
import Logo from "../Logo";

import { linkResolver } from "../../../utils/links";

import Item from "./Item";

const dialogButtonStyles = {
  variant: "buttons.link",
  width: 35,
  height: 35,
  mr: "-9px",
  ".headroom--scrolled &": {
    color: "text",
    "&:hover": {
      color: "primary",
    },
  },
  "&:hover": {
    color: "primary",
  },
};

const MobileMenu = ({ navLinks, button, onToggle = () => {}, sx }) => {
  const dialog = useDialogState({
    unstable_fixed: true,
    animated: true,
  });

  const isVisible = React.useRef(dialog.visible);
  const controls = useAnimation();
  const menuSize = useResponsiveValue([300, 400]);

  const variants = {
    visible: { x: -menuSize },
    hidden: { x: 0 },
  };

  React.useEffect(() => {
    if (isVisible.current === dialog.visible) return;
    onToggle(dialog.visible);

    controls.start(dialog.visible ? "visible" : "hidden");

    isVisible.current = dialog.visible;
  }, [dialog.visible, onToggle, controls]);

  return (
    <React.Fragment>
      <DialogDisclosure
        {...dialog}
        as={Button}
        variant="reset"
        sx={{ ...dialogButtonStyles, ...sx }}
        aria-label="open sidebar"
      >
        <Hamburger height={16} />
      </DialogDisclosure>
      <DialogBackdrop
        {...dialog}
        sx={(theme) => ({
          position: "fixed",
          zIndex: "modal",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "white",
          transition: (theme) => `all ${theme.transitions.normal}`,
          opacity: 0,
          "&[data-enter]": {
            opacity: 0.5,
          },
        })}
      />
      <Dialog {...dialog} aria-label="Menu" tabIndex={0}>
        <motion.div
          drag="x"
          dragConstraints={{ left: -menuSize, right: 0 }}
          animate={controls}
          variants={variants}
          initial={false}
          onDragEnd={(e, info) => {
            if (info.offset.x >= 150) {
              dialog.hide();
            } else {
              controls.start("visible");
            }
          }}
          dragElastic={0.2}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          sx={(theme) => ({
            position: "fixed",
            zIndex: "modal",
            top: 0,
            bottom: 0,
            left: "100%",
            width: menuSize,
            backgroundColor: theme.colors.background,
            "&:after": {
              position: "absolute",
              top: 0,
              left: "100%",
              display: "block",
              width: "100%",
              height: "100%",
              content: "' '",
              backgroundColor: theme.colors.background,
            },
          })}
        >
          <Box
            sx={{
              height: "100%",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <GridRow
              columns={4}
              sx={{
                width: "100%",
                height: "100%",
                gridTemplateRows: "1fr auto",
              }}
            >
              <GridRow.Col
                fullWidth
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Box sx={{ position: "relative", display: "flex", py: [3, 4] }}>
                  <Box
                    sx={(theme) => ({
                      position: "relative",
                      display: ["flex", "flex", "none"],
                      mr: "auto",
                      alignItems: "center",
                    })}
                  >
                    <Link to="/">
                      <Logo />
                    </Link>
                  </Box>
                  <Button
                    variant="reset"
                    sx={{
                      ...dialogButtonStyles,
                      position: "absolute",
                      right: 0,
                      alignSelf: "center",
                    }}
                    onClick={dialog.hide}
                  >
                    <Close />
                  </Button>
                </Box>
                <List>
                  {navLinks.map((link, i) => {
                    const linkProps = linkResolver(link);
                    return (
                      <Item
                        key={linkProps.to || linkProps.href}
                        style={{ "--index": i }}
                        linkProps={linkProps}
                        link={link}
                      />
                    );
                  })}
                  <Box as="li" sx={{ mt: 3, mb: "gridGap" }}>
                    <Button variant="inverted" {...linkResolver(button)} />
                  </Box>
                </List>
              </GridRow.Col>
            </GridRow>
          </Box>
        </motion.div>
      </Dialog>
    </React.Fragment>
  );
};

export default MobileMenu;
